import { Box, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { forwardRef } from 'react';

interface HelpOptionCardProps {
  title: string;
  description: string;
  imageSrc: string;
  iconBg: string;
  imageAlignTopRight?: boolean;
  selected: boolean;
  onClick: () => void;
  testId: string;
  tabIndex?: number;
}

const HelpOptionCard = forwardRef<HTMLDivElement, HelpOptionCardProps>(
  (
    {
      title,
      description,
      imageSrc,
      iconBg,
      imageAlignTopRight = false,
      selected,
      onClick,
      testId,
      tabIndex = 0,
    },
    ref,
  ) => {
    return (
      <Box
        ref={ref}
        role='radio'
        aria-checked={selected}
        tabIndex={tabIndex}
        onClick={onClick}
        data-testid={testId}
        sx={[
          {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            width: '100%',
            minHeight: 200,
            px: 2,
            py: 3,
            pt: 4.5,
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '2px solid transparent',
            boxShadow:
              '0 -2px 1px 0 rgba(8,17,34,0.04), 0 2px 4px 0 rgba(8,17,34,0.20)',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow:
                '0 -4px 2px 0 rgba(8,17,34,0.04), 0 6px 12px 0 rgba(8,17,34,0.20)',
              borderColor: 'primary.400',
            },
            '&:focus-visible': {
              outline: (theme) => `3px solid ${theme.palette.primary[300]}`,
              outlineOffset: '2px',
            },
          },
          selected && {
            borderColor: 'primary.main',
            '&:hover': { borderColor: 'primary.main' },
          },
        ]}
      >
        <Box
          aria-hidden
          data-testid={`${testId}-radio`}
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: '2px solid',
            borderColor: selected ? 'primary.main' : 'grey.400',
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          {selected && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
              }}
            />
          )}
        </Box>

        <Stack
          alignItems='center'
          justifyContent='center'
          data-testid={`${testId}-icon`}
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            backgroundColor: iconBg,
            mb: 1.5,
            overflow: 'clip',
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <img
            src={imageSrc}
            alt=''
            aria-hidden
            style={{
              width: imageAlignTopRight ? '80%' : '68%',
              height: imageAlignTopRight ? '80%' : '68%',
              objectFit: 'contain',
              objectPosition: imageAlignTopRight ? 'right top' : 'center',
              position: 'absolute',
              top: imageAlignTopRight ? 0 : 'auto',
              right: imageAlignTopRight ? 0 : 'auto',
            }}
          />
        </Stack>

        <Typography variant='b1Medium' sx={{ color: 'grey.800', mb: 0.5 }}>
          {title}
        </Typography>
        <Typography
          variant='b2Regular'
          sx={{ color: 'grey.600', alignSelf: 'stretch', overflow: 'hidden' }}
        >
          {description}
        </Typography>
      </Box>
    );
  },
);

HelpOptionCard.displayName = 'HelpOptionCard';

export default HelpOptionCard;
